import React, { useContext, useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  withStyles,
} from '@material-ui/core';
import NumberFormat from 'react-number-format';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { colors, fonts } from 'theme';
import { useActiveContractToDate, useEstimatedReceptionsWithFilter } from 'hooks';
import { EstimatedReceptionForm } from 'types';
import WeightInputField from 'components/CustomInputs/WeightInputField';
import { useLatestClosure, useLatestMonthAndYear, useMonthAndYearToClose } from 'utils/closureHooks';
import { getMonthlyReception } from 'utils/closureData';
import { AppContext } from 'context';
import { formatMonthForDisplay } from 'utils/date';
import { estimatedReceptionFormSchema } from './EstimatedReceptionEditForm.yup';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    paddingBottom: '2.5em',
  },
  estimatedReceptionsTable: {
    paddingTop: '100px',
    width: '65%',
    borderTop: `1px solid ${colors.greyLighten20}`,
    overflow: 'hidden',
  },
  headerCell: {
    padding: '11px 22px',
    '&:not(:last-child)': {
      borderRight: `1px solid ${colors.greyLighten20}`,
    },
  },
  bodyCell: {
    padding: '2px 22px',
    border: `1px solid ${colors.greyLighten20}`,
    maxHeight: 3,
    '&:first-child': {
      borderLeft: 'none',
    },
    '&:last-child': {
      borderRight: 'none',
    },
  },
  clientNameCell: {
    width: '30%',
  },
  streamNameCell: {
    width: '20%',
  },
  previousMonthReceptionCell: {
    padding: '7px 22px',
    width: '25%',
  },
  estimatedReceptionCell: {
    width: '25%',
  },
  muiTextFieldRoot: {
    '& input': {
      textAlign: 'right',
      fontFamily: fonts.secondary,
    },
  },
  alignRight: {
    textAlign: 'right',
  },
});

const StyledTableCell = withStyles({
  root: {
    fontFamily: fonts.secondary,
    fontSize: '16px',
  },
})(TableCell);

export interface Props {
  onSubmit: SubmitHandler<EstimatedReceptionForm>;
  readOnly: boolean;
}

const EstimatedReceptionEditForm: React.FC<Props> = ({ onSubmit, readOnly }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'estimatedReceptions' });
  const classes = useStyles();
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const schema = useMemo(() => estimatedReceptionFormSchema(t), [t]);

  const { control, handleSubmit, setValue } = useForm<EstimatedReceptionForm>({
    mode: 'onBlur',
    resolver: yupResolver(schema),
  });

  const [latestClosure, isClosureLoading] = useLatestClosure({ 'groups[]': 'with_closure_data' });
  const [latestMonth, latestYear] = useLatestMonthAndYear(sortingCenter, latestClosure, isClosureLoading);
  const [nextMonth, nextYear] = useMonthAndYearToClose(sortingCenter, latestClosure, isClosureLoading);

  const contractsDateToFetch = useMemo(() => {
    if (!nextMonth || !nextYear) {
      return null;
    }
    // Get Last day of the month
    const d = new Date(nextYear, nextMonth, 0);
    d.setHours(12);
    return d;
  }, [nextMonth, nextYear]);

  useEffect(() => {
    if (!sortingCenter) return;
    setValue('sortingCenter', sortingCenter['@id']);
  }, [setValue, sortingCenter]);

  useEffect(() => {
    if (!nextMonth) return;
    setValue('month', nextMonth);
  }, [setValue, nextMonth]);

  useEffect(() => {
    if (!nextYear) return;
    setValue('year', nextYear);
  }, [setValue, nextYear]);

  const activeContracts = useActiveContractToDate(contractsDateToFetch);
  const estimatedReceptions = useEstimatedReceptionsWithFilter(nextMonth, nextYear);

  return (
    <form id="estimated_receptions_form" onSubmit={handleSubmit(onSubmit)} className={classes.form}>
      {estimatedReceptions && activeContracts && latestMonth && latestYear && nextMonth && nextYear && (
        <TableContainer className={classes.estimatedReceptionsTable}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell className={`${classes.headerCell} ${classes.clientNameCell}`}>
                  {t('clientName')}
                </StyledTableCell>
                <StyledTableCell className={`${classes.headerCell} ${classes.streamNameCell}`}>
                  {t('streamName')}
                </StyledTableCell>
                <StyledTableCell
                  className={`${classes.headerCell} ${classes.previousMonthReceptionCell} ${classes.alignRight}`}
                >
                  <span>{t('previousMonthReception')}</span>
                  <br />
                  <span>{`${formatMonthForDisplay(latestMonth, latestYear)} ${latestYear}`}</span>
                </StyledTableCell>
                <StyledTableCell
                  className={`${classes.headerCell} ${classes.estimatedReceptionCell} ${classes.alignRight}`}
                >
                  {t('estimatedReceptionPart1')}
                  <br />
                  {t('estimatedReceptionPart2', { month: formatMonthForDisplay(nextMonth, nextYear), year: nextYear })}
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeContracts.map((activeContract, index) => {
                const estimatedReception = estimatedReceptions.find(
                  reception =>
                    reception.client.id === activeContract.client.id &&
                    reception.stream.id === activeContract.stream.id,
                );
                const monthlyReception =
                  estimatedReception && latestClosure?.data
                    ? getMonthlyReception(latestClosure.data, estimatedReception.client, estimatedReception.stream) /
                      1000
                    : null;
                if (estimatedReception) {
                  setValue(`estimatedReceptions.${index}.id`, estimatedReception?.['@id']);
                }
                setValue(`estimatedReceptions.${index}.client`, activeContract.client['@id']);
                setValue(`estimatedReceptions.${index}.stream`, activeContract.stream['@id']);
                return (
                  <TableRow key={`estimatedReceptions.${activeContract.id}`}>
                    <StyledTableCell className={`${classes.bodyCell} ${classes.clientNameCell}`}>
                      {activeContract.client.shortName}
                    </StyledTableCell>
                    <StyledTableCell className={`${classes.bodyCell} ${classes.streamNameCell}`}>
                      {activeContract.stream.name}
                    </StyledTableCell>
                    <StyledTableCell
                      className={`${classes.bodyCell} ${classes.previousMonthReceptionCell} ${classes.alignRight}`}
                    >
                      {monthlyReception ? (
                        <NumberFormat
                          decimalSeparator=","
                          decimalScale={3}
                          isNumericString
                          suffix=" t"
                          fixedDecimalScale={true}
                          displayType="text"
                          value={monthlyReception}
                        />
                      ) : (
                        '-'
                      )}
                    </StyledTableCell>
                    <StyledTableCell
                      className={`${classes.bodyCell} ${classes.estimatedReceptionCell} ${classes.alignRight}`}
                    >
                      {readOnly ? (
                        <NumberFormat
                          decimalSeparator=","
                          decimalScale={3}
                          isNumericString
                          suffix=" t"
                          fixedDecimalScale={true}
                          displayType="text"
                          value={estimatedReception ? estimatedReception.weight / 1000 : 0}
                        />
                      ) : (
                        <Controller
                          name={`estimatedReceptions.${index}.weight`}
                          control={control}
                          shouldUnregister
                          render={({ field: { ref, ...inputProps }, fieldState }) => (
                            <WeightInputField
                              className={classes.muiTextFieldRoot}
                              inputRef={ref}
                              defaultValue={estimatedReception ? estimatedReception.weight / 1000 : 0}
                              {...inputProps}
                              helperText={fieldState.error?.message}
                              error={fieldState.invalid}
                            />
                          )}
                        />
                      )}
                    </StyledTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </form>
  );
};
export default EstimatedReceptionEditForm;
